import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  themeQuartz,
  type CellStyle,
  type ColDef,
  type ColumnState,
  type GridApi,
  type GridReadyEvent,
  type IHeaderParams,
  type ICellRendererParams,
  type SortChangedEvent,
  type ValueFormatterParams,
} from "ag-grid-community";
import ax from "../../../config/axios";
import DualLineChart from "./DualLineChart";
import SetFilter from "./SetFilter";
// Side-effect import: emits the :global AG Grid overrides (cell overflow +
// hovered-row z-index lift) that let the recharts tooltip escape the cell and
// paint above adjacent rows. Without this the tooltip clips behind neighbors.
import "./TableShowcase.module.css";



const agTheme = themeQuartz.withParams({
  columnBorder: { style: "solid", width: 1, color: "#e0e0e0" },
  headerColumnBorder: false,
  oddRowBackgroundColor: "#f8f9fa",
});


const CATEGORY_COLORS: Record<string, string> = {
  GRAINS: "#d4a373",
  INDICIES: "#6c757d",
  FINANCIALS: "#4e79a7",
  SOFTS: "#c492b1",
  METALS: "#b5b5b5",
  LIVESTOCK: "#a0522d",
  ENERGIES: "#ff7f0e",
  CURRENCIES: "#2ca02c",
};

// COT data is flagged stale when the latest report is older than this many days.
const STALE_DAYS = 14;
const DAY_MS = 86_400_000;

const TABULAR = { fontVariantNumeric: "tabular-nums" as const };

const GREEN_CELL: CellStyle = { color: "#198754", ...TABULAR };
const RED_CELL: CellStyle = { color: "#dc3545", ...TABULAR };
const CHART_CELL: CellStyle = {
  overflow: "visible",
  position: "relative",
  zIndex: 1,
};

const fmtInt = (v: number | null | undefined) =>
  v == null ? "" : Math.round(v).toLocaleString();

const fmtSignedInt = (v: number | null | undefined) =>
  v == null ? "" : (v > 0 ? "+" : "") + Math.round(v).toLocaleString();

const fmtPct = (v: number | null | undefined, digits = 2) =>
  v == null ? "" : v.toFixed(digits) + "%";

const fmtSignedPct = (v: number | null | undefined, digits = 2) =>
  v == null ? "" : (v > 0 ? "+" : "") + v.toFixed(digits) + "%";

// Green/grey/red based on the cell value's sign (matches the COT card colors).
const colorBySign = (p: { value: number | null }): CellStyle => ({
  color:
    p.value != null && p.value > 0
      ? "#198754"
      : p.value != null && p.value < 0
        ? "#dc3545"
        : "#6c757d",
  ...TABULAR,
});

// Mirrors the COT card's "Roll Start" coloring: past roll-end is red, in the
// roll window is amber, otherwise green/red by days-until.
const rollCellStyle = (p: { value: number | null; data?: CotRow }): CellStyle => {
  const v = p.value;
  if (v == null) return { color: "#6c757d", ...TABULAR };
  const end = p.data?.rollEndDate;
  if (end) {
    const daysToEnd = Math.floor((new Date(end).getTime() - Date.now()) / DAY_MS);
    if (daysToEnd < 0) return { color: "#dc3545", ...TABULAR };
    if (v <= 0) return { color: "#fd7e14", ...TABULAR };
  }
  return { color: v < 0 ? "#dc3545" : "#198754", ...TABULAR };
};

const RootCellRenderer = (p: ICellRendererParams<CotRow>) =>
  p.data ? (
    <span className="d-flex align-items-center gap-1">
      <span className="fw-semibold">{p.data.root}</span>
      {p.data.isStale && (
        <i
          className="bi bi-exclamation-triangle-fill text-warning"
          title="COT data for this root is stale (no update in the last 2 weeks)."
        />
      )}
    </span>
  ) : null;

const DualCellRenderer = (p: ICellRendererParams<CotRow>) =>
  p.data ? <DualLineChart data={p.data.history} height={52} /> : null;

/** One point of the COT chart series returned by /data/cot-analysis. */
interface CotPoint {
  Date: string;
  MM_Net: number;
  close: number;
}

/** Raw /data/cot-analysis row shape (subset consumed here). */
interface CotApiRow {
  id?: number;
  root: string;
  name: string;
  category: string;
  latest: number;
  net_change: number;
  pct: number;
  pct_abs: number;
  pct_oi: number;
  pct_relative: number;
  relative_change: number;
  max_long: number;
  max_short: number;
  latest_oi: number;
  latest_oi_date: string;
  roll_start_date: string | null;
  roll_end_date: string | null;
  data: CotPoint[];
}

/** Flattened row matching the metrics shown on the COT analysis cards. */
interface CotRow {
  id: number;
  root: string;
  marketName: string;
  category: string;
  netPosition: number;
  netChange: number;
  sidePct: number;
  magnitudePct: number;
  relativePct: number;
  relativeChange: number;
  maxLong: number;
  maxShort: number;
  openInterest: number;
  openInterestDate: string | null;
  mmPctOi: number;
  daysToRoll: number | null;
  rollStartDate: string | null;
  rollEndDate: string | null;
  isStale: boolean;
  history: { i: number; price: number; mm: number }[];
}

/**
 * Maps the COT analysis API rows into the flattened CotRow shape. The chart
 * series (close + MM_Net) feeds the MM/Price dual-line cell. `daysToRoll` and
 * `isStale` are derived the same way the COT cards compute them.
 */
function mapCotRows(rows: CotApiRow[] = [], lastUpdate: Date | null): CotRow[] {
  const staleBefore = lastUpdate ? lastUpdate.getTime() - STALE_DAYS * DAY_MS : null;

  return rows.map((r, idx) => {
    const history = (r.data ?? []).map((p, i) => ({
      i,
      price: p.close,
      mm: p.MM_Net,
    }));

    const lastDate = r.data?.length
      ? new Date(r.data[r.data.length - 1].Date).getTime()
      : null;
    const isStale =
      staleBefore != null && lastDate != null && lastDate < staleBefore;

    const daysToRoll = r.roll_start_date
      ? Math.floor((new Date(r.roll_start_date).getTime() - Date.now()) / DAY_MS)
      : null;

    return {
      id: r.id ?? idx + 1,
      root: r.root,
// COT names are "CORN - CHICAGO BOARD OF TRADE"; show just the market name.
      marketName: r.name?.includes("-")
        ? r.name.slice(0, r.name.lastIndexOf("-")).trim()
        : r.name,
      category: r.category,
      netPosition: r.latest,
      netChange: r.net_change,
      sidePct: r.pct,
      magnitudePct: r.pct_abs,
      relativePct: r.pct_relative,
      relativeChange: r.relative_change,
      maxLong: r.max_long,
      maxShort: r.max_short,
      openInterest: r.latest_oi,
      openInterestDate: r.latest_oi_date ?? null,
      mmPctOi: r.pct_oi,
      daysToRoll,
      rollStartDate: r.roll_start_date ?? null,
      rollEndDate: r.roll_end_date ?? null,
      isStale,
      history,
    };
  });
}

interface SortPreset {
  label: string;
  icon: string;
  colId: string;
  sort: "asc" | "desc";
}

const SORT_PRESETS: SortPreset[] = [
  { label: "Position Side", icon: "bi-arrow-down-up", colId: "sidePct", sort: "desc" },
  { label: "Net Change", icon: "bi-activity", colId: "netChange", sort: "desc" },
  { label: "Relative %", icon: "bi-percent", colId: "relativePct", sort: "desc" },
  { label: "MM % OI", icon: "bi-pie-chart", colId: "mmPctOi", sort: "desc" },
  { label: "Roll Start", icon: "bi-calendar-event", colId: "daysToRoll", sort: "asc" },
];

export function AgGridGrid() {
  const apiRef = useRef<GridApi<CotRow> | null>(null);

  const [rowData, setRowData] = useState<CotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track the grid's current single-column sort so macro buttons stay in sync.
  const [activeSort, setActiveSort] = useState<{ colId: string; sort: string } | null>(null);

  const syncActiveSort = useCallback(() => {
    const state = apiRef.current?.getColumnState();
    if (!state) return;
    const sorted = state.filter((c: ColumnState) => c.sort != null);
    if (sorted.length === 1) {
      setActiveSort({ colId: sorted[0].colId, sort: sorted[0].sort! });
    } else {
      setActiveSort(null);
    }
  }, []);

  const onSortChanged = useCallback(
    (_e: SortChangedEvent<CotRow>) => syncActiveSort(),
    [syncActiveSort],
  );

  const applyPresetSort = useCallback(
    (preset: SortPreset) => {
      const api = apiRef.current;
      if (!api) return;

      const isActive =
        activeSort?.colId === preset.colId && activeSort?.sort === preset.sort;

      if (isActive) {
        api.applyColumnState({ defaultState: { sort: null } });
      } else {
        api.applyColumnState({
          state: [{ colId: preset.colId, sort: preset.sort }],
          defaultState: { sort: null },
        });
      }
    },
    [activeSort],
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    ax.get("data/cot-analysis", { signal: controller.signal })
      .then((res) => {
        const lastUpdate = res.data.last_update
          ? new Date(res.data.last_update)
          : null;
        setRowData(mapCotRows(res.data.data, lastUpdate));
      })
      .catch((err) => {
        if (err.name === "CanceledError") return; // ignore unmount abort
        console.error("Error fetching COT data:", err);
        setError("Failed to load COT data.");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  const columnDefs = useMemo<ColDef<CotRow>[]>(
    () => [
      {
        field: "root",
        headerName: "Root",
        width: 110,
        pinned: "left",
        cellRenderer: RootCellRenderer,
      },
      {
        field: "marketName",
        headerName: "Market",
        flex: 1.4,
        minWidth: 150,
      },
      {
        field: "category",
        headerName: "Category",
        flex: 1,
        minWidth: 120,
        filter: SetFilter,
        cellRenderer: (p: ICellRendererParams<CotRow>) =>
          p.value ? (
            <span
              className="badge"
              style={{
                backgroundColor: CATEGORY_COLORS[p.value] ?? "#adb5bd",
                color: "#fff",
              }}
            >
              {p.value}
            </span>
          ) : null,
      },
      {
        field: "netPosition",
        headerName: "Net Position",
        flex: 1,
        minWidth: 130,
        cellClass: "ag-right-aligned-cell",
        headerTooltip:
          "Managed Money net position (long minus short) as of the latest COT report.",
        valueFormatter: (p: ValueFormatterParams) => fmtInt(p.value),
        cellStyle: colorBySign,
      },
      {
        field: "netChange",
        headerName: "Net Chg",
        flex: 1,
        minWidth: 110,
        cellClass: "ag-right-aligned-cell",
        headerTooltip: "Change in MM net position from the prior COT report.",
        valueFormatter: (p: ValueFormatterParams) => fmtSignedInt(p.value),
        cellStyle: colorBySign,
      },
      {
        field: "sidePct",
        headerName: "Side %",
        flex: 1,
        minWidth: 100,
        cellClass: "ag-right-aligned-cell",
        headerTooltip:
          "How close the net position is to its own historical extreme on the same side (100% = most long/short ever).",
        valueFormatter: (p: ValueFormatterParams) => fmtPct(p.value),
        cellStyle: colorBySign,
      },
      {
        field: "magnitudePct",
        headerName: "Magnitude %",
        flex: 1,
        minWidth: 120,
        cellClass: "ag-right-aligned-cell",
        headerTooltip:
          "Net position size relative to the largest extreme ever reached in either direction.",
        valueFormatter: (p: ValueFormatterParams) => fmtPct(p.value),
      },
      {
        field: "relativePct",
        headerName: "Relative %",
        flex: 1,
        minWidth: 110,
        cellClass: "ag-right-aligned-cell",
        headerTooltip:
          "Where positioning sits across its full historical short\u2194long range (\u2212100% = most short, +100% = most long).",
        valueFormatter: (p: ValueFormatterParams) => fmtPct(p.value),
        cellStyle: colorBySign,
      },
      {
        field: "relativeChange",
        headerName: "Rel Chg",
        flex: 1,
        minWidth: 110,
        cellClass: "ag-right-aligned-cell",
        headerTooltip:
          "Week-over-week swing in relative positioning since the prior COT report.",
        valueFormatter: (p: ValueFormatterParams) =>
          p.value != null ? `\u0394 ${fmtSignedPct(p.value)}` : "N/A",
        cellStyle: colorBySign,
      },
      {
        field: "maxLong",
        headerName: "Max Long",
        flex: 1,
        minWidth: 120,
        cellClass: "ag-right-aligned-cell",
        headerTooltip:
          "Largest net long position MM has historically held in this market.",
        valueFormatter: (p: ValueFormatterParams) => fmtInt(p.value),
        cellStyle: GREEN_CELL,
      },
      {
        field: "maxShort",
        headerName: "Max Short",
        flex: 1,
        minWidth: 120,
        cellClass: "ag-right-aligned-cell",
        headerTooltip:
          "Largest net short position MM has historically held in this market.",
        valueFormatter: (p: ValueFormatterParams) => fmtInt(p.value),
        cellStyle: RED_CELL,
      },
      {
        field: "openInterest",
        headerName: "Open Interest",
        flex: 1,
        minWidth: 140,
        cellClass: "ag-right-aligned-cell",
        headerTooltip: "Total open futures contracts as of the latest date.",
        valueFormatter: (p: ValueFormatterParams) => fmtInt(p.value),
        tooltipValueGetter: (p) =>
          p.data?.openInterestDate
            ? `As of ${new Date(p.data.openInterestDate).toLocaleDateString()}`
            : "",
      },
      {
        field: "mmPctOi",
        headerName: "MM % OI",
        flex: 1,
        minWidth: 110,
        cellClass: "ag-right-aligned-cell",
        headerTooltip:
          "MM net position as a percentage of total open interest.",
        valueFormatter: (p: ValueFormatterParams) => fmtPct(p.value),
        cellStyle: colorBySign,
      },
      {
        field: "daysToRoll",
        headerName: "Roll Start",
        flex: 1,
        minWidth: 130,
        cellClass: "ag-right-aligned-cell",
        headerTooltip:
          "Days until (or past) the start of the front-month contract roll.",
        comparator: (
          a: number | null,
          b: number | null,
          _nodeA: unknown,
          _nodeB: unknown,
          isDescending: boolean,
        ) => {
          // Nulls always sink to the bottom regardless of sort direction.
          if (a == null && b == null) return 0;
          const nullWeight = isDescending ? -1 : 1;
          if (a == null) return nullWeight;
          if (b == null) return -nullWeight;
          // Nearest absolute distance first; at equal distance favor upcoming (positive).
          const diff = Math.abs(a) - Math.abs(b);
          if (diff !== 0) return diff;
          // Same distance: upcoming (positive) before past (negative).
          return b - a;
        },
        cellRenderer: (p: ICellRendererParams<CotRow>) => {
          if (!p.data) return null;
          const v = p.data.daysToRoll;
          const style = rollCellStyle({ value: v, data: p.data });
          const fmtDate = (d?: string | null) =>
            d
              ? new Date(d).toLocaleDateString(undefined, {
                month: "2-digit",
                day: "2-digit",
              })
              : null;
          const start = fmtDate(p.data.rollStartDate);
          const end = fmtDate(p.data.rollEndDate);
          return (
            <div style={{ ...style, lineHeight: 1.25, textAlign: "right" }}>
              <div>
                {v != null
                  ? `${Math.abs(v)}d ${v > 0 ? "until" : "past"}`
                  : "N/A"}
              </div>
              {(start || end) && (
                <div style={{ fontSize: "0.7rem", color: "#6c757d" }}>
                  {start ?? "?"} - {end ?? "?"}
                </div>
              )}
            </div>
          );
        },
      },
      {
        colId: "mmtrend",
        headerName: "MM / Price",
        flex: 1.5,
        minWidth: 190,
        sortable: false,
        filter: false,
        cellRenderer: DualCellRenderer,
        // Let the recharts tooltip escape AG Grid's clipped cell.
        cellClass: "chartCellOverflow",
        cellStyle: CHART_CELL,
      },
      {
        colId: "details",
        headerName: "",
        width: 90,
        pinned: "right",
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: (p: ICellRendererParams<CotRow>) =>
          p.data ? (
            <Link
              to={`/COT-analysis/${p.data.root}`}
              className="btn btn-sm btn-outline-primary py-0 px-2"
              style={{ fontSize: "0.75rem" }}
            >
              Details
            </Link>
          ) : null,
      },
    ],
    [],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const onGridReady = useCallback(
    (e: GridReadyEvent<CotRow>) => {
      apiRef.current = e.api;
      syncActiveSort();
    },
    [syncActiveSort],
  );

  const reset = useCallback(() => {
    apiRef.current?.setFilterModel(null);
    apiRef.current?.resetColumnState();
  }, []);

  return (
    <div className="w-100 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
      <div className="card mb-2">
        <div className="card-body py-2 d-flex flex-wrap gap-2 align-items-center">
          <button className="btn btn-sm btn-outline-secondary" onClick={reset}>
            <i className="bi bi-arrow-counterclockwise me-1" />
            Reset
          </button>

          <span className="border-start ms-2 ps-2 d-flex flex-wrap gap-1 align-items-center">
            <span className="text-muted small me-1">Sort Macros:</span>
            {SORT_PRESETS.map((preset) => {
              const isActive =
                activeSort?.colId === preset.colId &&
                activeSort?.sort === preset.sort;
              return (
                <button
                  key={preset.colId}
                  className={`btn btn-sm ${isActive ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => applyPresetSort(preset)}
                  title={`${isActive ? "Remove" : "Apply"} ${preset.sort === "desc" ? "descending" : "ascending"} sort on ${preset.label}`}
                >
                  <i className={`bi ${preset.icon} me-1`} />
                  {preset.label}
                  {isActive && (
                    <i
                      className={`bi ${preset.sort === "desc" ? "bi-sort-down" : "bi-sort-up"} ms-1`}
                    />
                  )}
                </button>
              );
            })}
          </span>

          <span className="ms-auto text-muted small">
            Click the header menu icon to filter · drag headers to reorder ·
            drag edges to resize
          </span>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2" role="alert">
          {error}
        </div>
      )}

      <div id="ag-grid-container" className="flex-grow-1 pb-2" style={{ minHeight: 0 }}>
        <AgGridReact<CotRow>
          theme={agTheme}
          rowData={rowData}
          loading={loading}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          tooltipShowDelay={750}
          rowHeight={56}
          pagination
          paginationPageSize={Math.ceil(rowData.length / 100) * 100}
          paginationPageSizeSelector={[Math.ceil(rowData.length / 100) * 100]}
          onGridReady={onGridReady}
          onSortChanged={onSortChanged}
        />
      </div>
    </div>
  );
}

export default AgGridGrid;
